import { useContext, useState, useEffect, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { AuthContext } from 'contexts/AuthContext';
import { DropsContext } from 'contexts/DropsContext';
import VirtualDrop from 'components/VirtualDrop';
import InputMask from 'components/InputMask';
import Container from 'components/Container';
import Dropdown from 'components/Dropdown';
import Header from 'components/Header';
import Button from 'components/Button';
import Input from 'components/Input';
import api from 'services/api';
import styles from './styles';
import moment from 'moment';

const Item = ({ item, Line, style }) => {
  const color = item?.operacao === 'EXPEDIÇÃO' ? 'info' : 'success';
  const statusColor =
    item?.status === 'PENDENTE'
      ? 'warning'
      : item?.status === 'CONCLUIDO'
        ? 'success'
        : item?.status === 'CANCELADO'
          ? 'error'
          : 'info';
  const itens = item?.Itens || [];

  const totais = useMemo(() => {
    return itens?.reduce(
      (acc, x) => ({
        quantidade: acc?.quantidade + x?.quantidade,
        quantidade_embalada: acc?.quantidade_embalada + x?.quantidade_embalada,
        // quantidade_volumada: acc?.quantidade_volumada + x?.quantidade_volumada,
      }),
      {
        quantidade: 0,
        quantidade_embalada: 0,
        // quantidade_volumada: 0,
      }
    );
  }, []);

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box flex={1} display="flex" alignItems="center" gap={2}>
          <Chip
            size="small"
            color={color}
            variant="outlined"
            label={`${item?.operacao}${
              item?.lote ? ` • Nº ${item?.lote}` : ''
            }`}
          />
          <Typography fontWeight="bold" flex={1}>
            {item?.especie}
            {item?.documento ? ` • Nº ${item?.documento}` : ''}
          </Typography>
          {item?.status && (
            <Chip size="small" color={statusColor} label={item?.status} />
          )}
          {item?.dtemissao && moment(item?.dtemissao).isValid() && (
            <Typography variant="caption">
              {moment(item?.dtemissao).format('DD/MM/YYYY')}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    Referência
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    Produto
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    Quantidade
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itens?.map((i, x) => (
                <TableRow key={x?.toString()}>
                  <TableCell>{i?.referencia}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body1"
                      color="primary"
                      fontWeight="bold"
                      mb={
                        i?.variacao || i?.validade || i?.rastreabilidade ? 2 : 0
                      }
                    >
                      {i?.descricao}
                    </Typography>
                    <Line label="Variação" value={i?.variacao} />
                    <Line
                      label="Validade"
                      value={
                        i?.validade && moment(i?.validade).format('DD/MM/YYYY')
                      }
                    />
                    <Line label="Rastreabilidade" value={i?.rastreabilidade} />
                  </TableCell>
                  <TableCell align="right">
                    <Line
                      edge="end"
                      label="Total"
                      value={`${i?.quantidade || 0}${i?.um ? ` ${i?.um}` : ''}`}
                    />
                    <Line
                      edge="end"
                      label="Embalagens"
                      value={`${i?.quantidade_embalada || 0}${
                        i?.um ? ` ${i?.um}` : ''
                      }`}
                    />
                    {/* <Line
                      edge="end"
                      label="Volumes"
                      value={`${i?.quantidade_volumada || 0}${
                        i?.um ? ` ${i?.um}` : ''
                      }`}
                    /> */}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={style?.table}>
                <TableCell colSpan={2}>
                  <Typography variant="body2" fontWeight="bold">
                    Totais
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Line
                    edge="end"
                    label="Total"
                    value={`${totais?.quantidade || 0}`}
                  />
                  <Line
                    edge="end"
                    label="Embalagens"
                    value={`${totais?.quantidade_embalada || 0}`}
                  />
                  {/* <Line
                    edge="end"
                    label="Volumes"
                    value={`${totais?.quantidade_volumada || 0}`}
                  /> */}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};

const Movimentacoes = () => {
  const { empresa } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const defaultValues = {
    page: 0,
    size: 50,
    entidade_id: empresa,
    dti: null,
    dtf: null,
    produto_id: null,
    especie_id: null,
    documento: '',
  };
  const { control, handleSubmit, setValue, getValues } = useForm({
    defaultValues,
  });
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const style = styles(theme);

  const loadData = async (params) => {
    if (loading) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/WMS/Estoque/Movimentacoes`, { params });
      setMovimentacoes((prev) =>
        params?.page === 0 ? data : [...prev, ...data]
      );
      setHasMore(data?.length === params?.size);
      setValue('page', params?.page);
    } catch (error) {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (values) => {
    loadData({ ...values, page: 0 });
  };

  const onChangePage = (values) => {
    const { page } = getValues();
    const nextPage = (page || 0) + 1;
    loadData({ ...values, page: nextPage });
  };

  useEffect(() => {
    handleSubmit(loadData)();
  }, []);

  const Line = ({ label, value, edge = 'start' }) =>
    value && (
      <Box display="flex" gap={2} justifyContent={'flex-' + edge}>
        <Typography variant="caption" fontWeight="bold">
          {label}:
        </Typography>
        <Typography variant="caption">{value}</Typography>
      </Box>
    );

  return (
    <Container>
      <Grid container spacing={2} sx={style?.container}>
        <Grid item xs={12} md={3} sx={style?.filterContainer}>
          <form style={style?.flexContainer} onSubmit={handleSubmit(onSubmit)}>
            <Typography variant="h5">Filtros</Typography>
            <Divider />
            <Dropdown
              name="especie_id"
              label="Operação"
              control={control}
              options={[
                { label: 'ARMAZENAGEM', value: 54 },
                { label: 'EXPEDIÇÃO', value: 53 },
              ]}
            />
            <Input name="documento" label="Nº do Documento" control={control} />
            <VirtualDrop
              name="produto_id"
              label="Produto"
              control={control}
              options={drops?.Produto?.filter(
                (f) => f?.entidade_id === empresa
              )}
            />
            <Box display="flex" gap={2}>
              <Box flex={1}>
                <InputMask
                  type="date"
                  control={control}
                  label="Data Início"
                  name="dti"
                />
              </Box>
              <Box flex={1}>
                <InputMask
                  type="date"
                  control={control}
                  label="Data Fim"
                  name="dtf"
                />
              </Box>
            </Box>
            <Button
              type="submit"
              loading={loading}
              onClick={() => {}}
              variant="contained"
              sx={style?.formButton}
            >
              Filtrar
            </Button>
            <Divider />
          </form>
        </Grid>
        <Grid item xs={12} md={9} sx={style?.contentContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Header titulo="Movimentações" />
            </Grid>
            {!movimentacoes?.length && !loading && !hasMore && (
              <Grid item xs={12} textAlign="center">
                <Typography variant="caption">
                  Nenhuma movimentação encontrada.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              {loading && <LinearProgress />}
            </Grid>
            <Grid item xs={12}>
              {movimentacoes?.map((item, index) => (
                <Item
                  item={item}
                  Line={Line}
                  style={style}
                  key={index?.toString()}
                />
              ))}
            </Grid>
            {hasMore && (
              <Grid item xs={12} textAlign="center" pb={2}>
                <Button
                  loading={loading}
                  variant="contained"
                  onClick={handleSubmit(onChangePage)}
                >
                  Carregar mais
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Movimentacoes;
